/**
 * Collapse the asset registry into one entry per real-world asset.
 *
 * `AssetHolding` is a record, not an asset: the same position can arrive as
 * several rows — created from a loan, from a basket, or from the capacity tab —
 * and the registry has no unique constraint on `symbol`. A holder does not own
 * three DLEKG.TA positions; they own one, divided between the lenders it is
 * pledged to. The capacity view answers "how much of this asset do I have, and
 * how much of it is spoken for", so it has to group on the asset.
 *
 * The division itself lives on the pledges, which are what actually distinguish
 * one slice of a position from another: the lien holder, and the basket or loan
 * the slice sits in. Grouping merges the rows and keeps every pledge underneath,
 * tagged with the row it came from so an edit can still reach a single record.
 *
 * Only stocks merge. A symbol asserts that two rows are the same fungible thing;
 * nothing else in the registry carries that claim — two plots of land sharing a
 * name are two plots of land — so everything else stays a group of one.
 */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const firstNonNull = (values) => values.find((v) => v != null) ?? null;

/** The identity two holdings must share to be the same asset. */
export function assetGroupKey(holding) {
  const symbol = String(holding?.symbol || "").trim().toUpperCase();
  if (holding?.assetType === "STOCK" && symbol) return `STOCK:${symbol}`;
  return `ID:${holding?._id}`;
}

/**
 * @param {Array} holdings enriched holdings as returned by `GET /asset-holdings`
 * @returns {Array} one group per asset, sorted by type then name like the API
 */
export function groupHoldingsByAsset(holdings) {
  if (!Array.isArray(holdings) || holdings.length === 0) return [];

  const buckets = new Map();
  for (const holding of holdings) {
    const key = assetGroupKey(holding);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(holding);
  }

  const groups = [...buckets.entries()].map(([key, members]) => buildGroup(key, members));

  return groups.sort(
    (a, b) =>
      String(a.assetType).localeCompare(String(b.assetType)) ||
      String(a.assetName).localeCompare(String(b.assetName), "he")
  );
}

function buildGroup(key, members) {
  // Largest position first: it names the group and leads the source list, because
  // the row carrying the bulk of the units is the one a reader recognises.
  const sources = [...members].sort((a, b) => num(b.totalUnits) - num(a.totalUnits));
  const lead = sources[0];
  const isStock = lead.assetType === "STOCK";

  const pledges = sources
    .flatMap((source) =>
      (source.pledges || []).map((p) => ({
        ...p,
        sourceAssetId: source._id,
        sourceAssetName: source.assetName,
      }))
    )
    .sort((a, b) => num(b.pledgedUnits) - num(a.pledgedUnits));

  const group = {
    key,
    assetType: lead.assetType,
    assetName: lead.assetName,
    symbol: isStock ? String(lead.symbol || "").trim().toUpperCase() || null : null,
    notes: lead.notes || null,
    sources,
    isMerged: sources.length > 1,
    owningCompanies: [...new Set(sources.map((s) => (s.owningCompany || "").trim()).filter(Boolean))],
    pledges,
  };

  if (!isStock) {
    group.value = sources.reduce((s, h) => s + num(h.value), 0);
    group.annualCashFlow = sources.reduce((s, h) => s + num(h.annualCashFlow), 0);
    group.isPledged = pledges.length > 0;
    group.pledgedValue = group.isPledged ? group.value : 0;
    return group;
  }

  const totalUnits = sources.reduce((s, h) => s + num(h.totalUnits), 0);
  const pledgedUnits = sources.reduce((s, h) => s + num(h.pledgedUnits), 0);
  const freeUnits = sources.reduce((s, h) => s + num(h.freeUnits), 0);
  const restrictedUnits = sources.reduce((s, h) => s + num(h.restrictedUnits), 0);
  const usableUnits = sources.reduce((s, h) => s + num(h.usableUnits), 0);

  // Recomputed from the summed units — averaging the members' own percentages
  // would weight a 20,000-share row equally with a 93,000,000-share one.
  const pct = (units) => (totalUnits > 0 ? (units / totalUnits) * 100 : 0);

  // Members share a symbol, so they share a price. Null when no member has one:
  // an unpriced position has unknown value, which is not the same as zero.
  const currentPrice = firstNonNull(sources.map((h) => h.currentPrice));
  const value = (units) => (currentPrice == null ? null : units * currentPrice);

  Object.assign(group, {
    totalUnits, pledgedUnits, freeUnits, restrictedUnits, usableUnits,
    pledgedPct: pct(pledgedUnits),
    freePct: pct(freeUnits),
    restrictedPct: pct(restrictedUnits),
    usablePct: pct(usableUnits),

    currentPrice,
    priceChangePct: firstNonNull(sources.map((h) => h.priceChangePct)),
    stockPriceDate: firstNonNull(sources.map((h) => h.stockPriceDate)),
    logoUrl: firstNonNull(sources.map((h) => h.logoUrl)),
    marketCap: firstNonNull(sources.map((h) => h.marketCap)),

    totalValue: value(totalUnits),
    pledgedValue: value(pledgedUnits),
    freeValue: value(freeUnits),
    usableValue: value(usableUnits),
    restrictedValue: value(restrictedUnits),

    minimumFreeReason: firstNonNull(sources.map((h) => h.minimumFreeReason || null)),
  });

  group.pledges = pledges.map((p) => ({
    ...p,
    pctOfPosition: totalUnits > 0 ? (num(p.pledgedUnits) / totalUnits) * 100 : null,
  }));

  return group;
}
